import {ref, computed, watch} from "vue"
import {defineStore, storeToRefs, type SubscriptionCallbackMutation} from "pinia"
import {useRouter} from "vue-router"
import {useFetch, type ExtendedFetchOptions} from "../fetch"
import {useLocalStorage} from "@vueuse/core"
import {RoutesEnum} from "@/router/types"
import {useRestaurantStore} from "../restaurant"
import type {UserRoles} from "./types"

interface RestaurantUser {
  email?: string
  name?: string
  restaurantId?: string
  roles?: string[] // ["ADMIN_EDITOR", "RESTAURANT_ADMIN", "GUEST_ANALYTICS", "DELIVERY_MANAGE"]
  userId?: number | null
  options?: {
    restaurant_show_advanced_analytics: boolean
    crm_aida_enabled: boolean
    crm_guest_database_enabled: boolean
    crm_offers_enabled: boolean
  }
}

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter()
  const restaurantStore = useRestaurantStore()
  const {currentRestaurantId} = storeToRefs(restaurantStore)

  type RestaurantState = ReturnType<typeof useRestaurantStore>["$state"]

  // state
  const authToken = ref(useLocalStorage("token", ""))
  const browserId = ref(useLocalStorage("publickBrowserId", ""))
  const user = ref(
    useLocalStorage("user", {
      phone: "",
      infoByRestaurants: [] as RestaurantUser[],
    })
  )
  const userRoles = ref([] as string[])

  // getters
  const isLoggedIn = computed(() => !!authToken.value)
  const getBrowserId = computed(() => browserId.value)
  const getUserInfo = computed(() => user.value)
  const currentRestaurantOptions = computed(() =>
    user.value.infoByRestaurants.find((r) => r.restaurantId === currentRestaurantId.value)?.options
  )

  // actios
  async function login(token: string) {
    const {error: FetchError, data} = await useFetch(`/api/s2p/authorization/exchange-token`, {
      method: "POST",
      headers: {},
      body: JSON.stringify({
        oneTimeToken: token,
      }),
      $skipEncryption: false,
    } as ExtendedFetchOptions).get()

    if (data.value) {
      console.log("Login data: ", data.value)
      authToken.value = data.value.result.token
      await fetchUser()
    }

    if (FetchError.value) {
      authToken.value = ""
      throw FetchError.value
    }
  }
  async function fetchUser() {
    const {error: FetchError, data} = await useFetch(`/api/crm-analytics/auth-api/auth/me`, {
      method: "GET",
      headers: {},
      $skipEncryption: false,
      $useToken: true,
    } as ExtendedFetchOptions).get()

    if (data.value) {
      console.log("User data: ", data.value)
      user.value = data.value
    }

    if (FetchError.value) {
      console.log("Can't fetch user data", FetchError.value)
      throw FetchError.value
    }
  }
  async function logout() {
    const {error: FetchError, data} = await useFetch(`/api/s2p/authorization`, {
      method: "DELETE",
      $skipEncryption: false,
      $useToken: true,
    } as ExtendedFetchOptions).get()

    if (data.value) {
      resetUserAccess()
      console.log("Logout success!", data.value)
      router.push(RoutesEnum.UserLogin)
    }

    if (FetchError.value) {
      resetUserAccess()
      console.log("Logout anyway!", FetchError.value)
      throw FetchError.value
    }
  }
  function resetUserAccess() {
    authToken.value = ""
    user.value = {
      phone: "",
      infoByRestaurants: [],
    }
  }
  function updateUserCurrentRoles() {
    const restaurantRoles = user.value.infoByRestaurants.find(
      (r) => r.restaurantId === currentRestaurantId.value
    )
    userRoles.value = [...(restaurantRoles?.roles ?? [])]
  }
  function roleAccessGuard(roleArray: UserRoles[]) {
    return roleArray.some((r) => userRoles.value.includes(r))
  }

  watch(user, (newUser) => {
    console.log("WATCH USER", newUser)
    if (newUser) {
      let restaurantData
      if (newUser.infoByRestaurants.length) {
        restaurantData =
          newUser.infoByRestaurants.find((r) => r.restaurantId === currentRestaurantId.value) ||
          newUser.infoByRestaurants[0]

        restaurantStore.$patch({
          currentRestaurantId: restaurantData.restaurantId,
        })
      }
      updateUserCurrentRoles()
    }
  })

  restaurantStore.$subscribe((mutation: SubscriptionCallbackMutation<RestaurantState>, state) => {
    if (mutation.type === "patch object" && mutation.payload?.currentRestaurantId) {
      updateUserCurrentRoles()
    }
  })

  return {
    authToken,
    browserId,
    getBrowserId,
    getUserInfo,
    currentRestaurantOptions,
    isLoggedIn,
    userRoles,
    login,
    logout,
    fetchUser,
    resetUserAccess,
    roleAccessGuard,
  }
})
